import React, { useCallback, useRef } from 'react';
import { BaseScaleView, BaseStagePlugin, useFramework } from '@/framework';
import { type TextSelectionRange } from '@/utils/text-kit';
import { CARET_REVEAL_KEYS, MULTI_CLICK_GAP_MS } from '../constants';
import type { TextEditorPlugin } from '../manager';
import { type EditorTextSelection } from '../types';
import { getFallbackLineSize, getLogicalLineRange, getTextRangeFromPoint, getWordRange } from '../utils';
import { TextEditorGeometry, type Ref as TextEditorGeometryRef } from './geometry';
import { TextEditorTextarea, type TextEditorTextareaRef } from './textarea';
import { useLifecycle } from './use-lifecycle';
import { useSession } from './use-session';
import './editor.less';

export const TextEditorView = React.memo(() => {
    const framework = useFramework();
    const textEditor = framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');

    const session = useSession();

    const textareaRef = useRef<TextEditorTextareaRef>(null);
    const geometryRef = useRef<TextEditorGeometryRef>(null);
    const dragAnchorRef = useRef<number | null>(null);
    const clickRef = useRef<{ layerId: string; count: number; t: number; offset: number } | null>(null);

    const revealCaret = useCallback(() => geometryRef.current?.revealCaret(), []);

    const handleTextareaKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // 如果用户按下了垂直方向键，则立即显示光标，防止因为动画延迟导致光标不可见。
            if (CARET_REVEAL_KEYS.has(event.key)) revealCaret();
        },
        [revealCaret],
    );

    const setSelection = useCallback(
        (range: TextSelectionRange | EditorTextSelection) => {
            textEditor.setSelection(range);
        },
        [textEditor],
    );

    const style = session ? session.adapter.getDisplayStyle(session.editingLayer) : null;
    const minEditingHeight = style ? getFallbackLineSize(style.fontSize, style.lineSpacing) : 1;

    const exitEditing = useCallback(() => {
        return textareaRef.current?.exitEditing() ?? textEditor.exit();
    }, [textEditor]);

    useLifecycle({
        geometryRef,
        exitEditing,
    });

    /**
     * 使用已经同步收敛的 session，将客户端坐标转换为文本字节选区。
     *
     * session 作为参数显式传入，避免函数闭包继续读取 React 本轮 render 捕获的旧 snapshot；调用方在
     * 指针交互开始前通过 `flushLayout()` 获得与最新文本工作值对应的几何。
     *
     * @param point - 浏览器客户端坐标。
     * @param current - 已完成 pending layout 收敛的当前编辑会话。
     * @returns 命中的折叠文本选区；坐标无法转换时返回 null。
     */
    const getRangeFromClientPoint = (
        point: { x: number; y: number },
        current: NonNullable<ReturnType<TextEditorPlugin['getSession']>>,
    ) => {
        const svgPoint = geometryRef.current?.getPointFromClient(point);
        if (!svgPoint) return null;

        return getTextRangeFromPoint(current.snapshot, svgPoint);
    };

    const recordClick = (offset: number) => {
        if (!session) return 1;

        const now = performance.now();
        const prev = clickRef.current;
        const count =
            prev &&
            prev.layerId === session.editingLayer.id &&
            now - prev.t < MULTI_CLICK_GAP_MS &&
            Math.abs(prev.offset - offset) < 4
                ? prev.count + 1
                : 1;

        clickRef.current = count >= 3 ? null : { layerId: session.editingLayer.id, count, t: now, offset };
        return count;
    };

    const stopDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
        dragAnchorRef.current = null;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    /**
     * 处理编辑区域的指针按下：定位光标、双击选词、三击选行与 shift 扩选。
     *
     * 指针命中依赖最新 glyph 几何，因此进入分支前同步 flush pending layout，并在后续选词、选行和
     * shift 扩选中统一使用返回的 session，不能混用 React render 时捕获的旧文本或旧 selection。
     *
     * @param event - React 指针事件。
     * @returns 无返回值。
     */
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        const current = textEditor.flushLayout();
        if (event.button !== 0 || !current) return;

        event.preventDefault();
        event.stopPropagation();

        revealCaret();
        textareaRef.current?.resetDesiredInline();

        const range = getRangeFromClientPoint({ x: event.clientX, y: event.clientY }, current);
        if (!range) return;

        const clickCount = recordClick(range.end);
        dragAnchorRef.current = null;

        if (clickCount === 2) {
            setSelection(getWordRange(current.adapter.getText(current.editingLayer), range.end));
            return;
        }

        if (clickCount >= 3) {
            setSelection(getLogicalLineRange(current.adapter.getText(current.editingLayer), range.end));
            return;
        }

        if (event.shiftKey) {
            const anchor = current.selection.anchor;
            setSelection({
                start: Math.min(anchor, range.end),
                end: Math.max(anchor, range.end),
                anchor,
                focus: range.end,
            });
            return;
        }

        dragAnchorRef.current = range.end;
        event.currentTarget.setPointerCapture(event.pointerId);
        setSelection(range);
    };

    /**
     * 使用最新排版几何更新拖选终点。
     *
     * 拖动期间可能同时发生输入或 Renderer 失效；每次需要把客户端坐标映射为文本 offset 前都先
     * flush pending layout，确保拖选只消费当前 session 的 snapshot。
     *
     * @param event - React 指针移动事件。
     * @returns 无返回值。
     */
    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const anchor = dragAnchorRef.current;
        if (anchor === null) return;

        event.preventDefault();
        event.stopPropagation();

        const current = textEditor.flushLayout();
        if (!current) return;
        const range = getRangeFromClientPoint({ x: event.clientX, y: event.clientY }, current);
        if (!range) return;

        setSelection({
            start: Math.min(anchor, range.end),
            end: Math.max(anchor, range.end),
            anchor,
            focus: range.end,
        });
    };

    if (!session) return null;
    const vertical = session.snapshot?.writingMode === 'vertical-rl';

    return (
        <BaseStagePlugin enablePointerEvents name="text-editor" style={{ overflow: 'clip' }} zIndex={6}>
            <BaseScaleView layer={session.editingLayer} style={{ pointerEvents: 'initial' }}>
                <div
                    className="text-editor"
                    style={vertical ? { minWidth: minEditingHeight } : { minHeight: minEditingHeight }}
                    onContextMenu={(event) => {
                        event.stopPropagation();
                        textareaRef.current?.prepareContextMenu(event);
                    }}
                    onMouseDown={(event) => {
                        if (event.button !== 2) return;
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={stopDrag}
                    onPointerCancel={stopDrag}
                >
                    <TextEditorGeometry ref={geometryRef} />
                    <TextEditorTextarea
                        ref={textareaRef}
                        minHeight={minEditingHeight}
                        onKeyDown={handleTextareaKeyDown}
                    />
                </div>
            </BaseScaleView>
        </BaseStagePlugin>
    );
});

TextEditorView.displayName = 'TextEditorView';
